import React, { useState, useEffect, useCallback } from "react";
import { Button, Box } from "@mui/material";
import ListSelect from "../../../components/Utils/ListSelect";
import TextInput from "../../../components/Utils/TextInput";
import AutocompleteComponent from "../../../components/Utils/AutocompleteComponent";
import { postBlockId, postBlockItems } from "../../../components/Api/Post";
import BlockItemsTransfer from "./BlockItemsTransfer";
import { useDepartmentsQuery } from "../../../Hooks/useDepartmentsQuery";
import isAuthenticated from "../../../components/Utils/isAuthenticated";

const courseTypes = ["LEC", "LAB", "CLN", "LEC:LAB", "LEC/LAB/SEM"];
const locationTypes = ["In-person", "On-line"];
const blkid3Types = ["3SPOT", "3STA", "3OPN", "3CSJ", "3CSJO", "NA"];
const originTypes = ["BASIC", "CUSTOM"];
const langTypes = ["EN", "FR"];
const csizeTypes = ["SMALL", "LARGE", "EITHER"];

export default function AddBlocks() {
	const initialBlockState = {
		catalog: "GFC",
		blkid: "",
		baddr1: "",
		baddr2: "",
		status: "C",
		ctype: "",
		faculty: "",
		dept_id: "",
		c_location: "",
		course: "",
		blkid3: "",
		origin: "CUSTOM",
		lang: "EN",
		csize: "EITHER",
	};
	const auth = isAuthenticated();
	const token = auth.token;

	// Use null for clarity with the autocomplete value
	const [autocompleteValue, setAutocompleteValue] = useState(null);
	const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

	// Centralized form state
	const [blockState, setBlockState] = useState(initialBlockState);
	const [chosenItems, setChosenItems] = useState([]);

	const { data: departments } = useDepartmentsQuery(token);

	// Memoize change handler so children do not re-render unnecessarily.
	const handleChange = useCallback((event) => {
		const { name, value } = event.target;
		setBlockState((prev) => ({
			...prev,
			[name]: value,
		}));
	}, []);

	// When the autocomplete value changes, update corresponding fields in our block state.
	useEffect(() => {
		if (autocompleteValue) {
			setBlockState((prev) => ({
				...prev,
				dept_id: autocompleteValue.key,
				baddr1: autocompleteValue.display_name,
				faculty: autocompleteValue.faculty,
			}));
		}
	}, [autocompleteValue]);

	// Async call for posting data is memoized to avoid re-creation.
	const asyncPostBlockId = useCallback(async (token, block, blockItems) => {
		try {
			await postBlockId(token, block);
			await postBlockItems(token, blockItems);
		} catch (err) {
			console.error("Submission error", err);
		}
	}, []);

	const handleSubmit = useCallback(() => {
		// Prepare the block object using state – values from autocomplete take precedence.
		const block = {
			...blockState,
			baddr1: autocompleteValue?.display_name || blockState.baddr1,
			faculty: autocompleteValue?.faculty || blockState.faculty,
			dept_id: autocompleteValue?.key || blockState.dept_id,
		};

		// Build blockItems array from chosen items.
		const blockItems = chosenItems.map((item, index) => ({
			blkid: block.blkid,
			q_order: index + 1,
			itemno: item.itemno,
		}));

		asyncPostBlockId(token, block, blockItems);

		// Reset states after submit
		setBlockState(initialBlockState);
		setChosenItems([]);
		setAutocompleteValue(null);
		setAutocompleteInputValue("");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blockState, chosenItems, autocompleteValue, token]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "flex-start",
				width: "100%",
			}}
		>
			<Box
				sx={{
					marginTop: "2%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<Box
					sx={{
						width: "100%",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<AutocompleteComponent
						options={departments || []}
						value={autocompleteValue}
						setValue={setAutocompleteValue}
						inputValue={autocompleteInputValue}
						setInputValue={setAutocompleteInputValue}
						label="Department"
					/>

					<TextInput
						value={blockState.blkid}
						name="blkid"
						label="Block ID"
						onChange={handleChange}
					/>

					<ListSelect
						value={blockState.ctype}
						name="ctype"
						label="Type"
						onChange={handleChange}
						list={courseTypes}
					/>

					<ListSelect
						value={blockState.c_location}
						name="c_location"
						label="Location"
						onChange={handleChange}
						list={locationTypes}
					/>

					<TextInput
						value={blockState.course}
						name="course"
						label="Course"
						onChange={handleChange}
					/>

					<ListSelect
						value={blockState.blkid3}
						name="blkid3"
						label="Standard Block"
						onChange={handleChange}
						list={blkid3Types}
					/>

					<ListSelect
						value={blockState.origin}
						name="origin"
						label="Origin"
						onChange={handleChange}
						list={originTypes}
					/>

					<ListSelect
						value={blockState.lang}
						name="lang"
						label="Language"
						onChange={handleChange}
						list={langTypes}
					/>

					<ListSelect
						value={blockState.csize}
						name="csize"
						label="CSize"
						onChange={handleChange}
						list={csizeTypes}
					/>

					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							marginTop: "10px",
							marginBottom: "10px",
						}}
					>
						<BlockItemsTransfer
							chosenItems={chosenItems}
							setChosenItems={setChosenItems}
						/>
					</Box>

					<Button
						variant="contained"
						sx={{ display: "flex", width: "100%", marginBottom: "20px" }}
						onClick={handleSubmit}
					>
						Submit
					</Button>
				</Box>
			</Box>
		</Box>
	);
}
